import { Injectable, inject } from '@angular/core';
import { NativeDateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import moment, { Moment } from 'moment-timezone';
import { DateFormatService } from './date-format.service';

/**
 * Custom date adapter that delegates parse/format to moment using a dynamic
 * date format string supplied by DateFormatService (from API / store).
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  private currentFormat: string;
  private readonly defaultFormat = 'DD/MM/YYYY';
  private readonly defaultTimeFormat = 'h.mm A';
  private readonly matDateLocale = inject(MAT_DATE_LOCALE);
  private readonly dateFormatService = inject(DateFormatService);

  constructor() {
    super();
    this.setLocale(this.matDateLocale as any);
    this.currentFormat = this.dateFormatService.currentFormat || this.defaultFormat;
    // React to runtime changes
    this.dateFormatService.format$().subscribe((fmt) => {
      this.currentFormat = fmt || this.defaultFormat;
      // Trigger UI update by emitting locale change (hacky but effective)
      this.setLocale(this.locale);
    });
  }

  /** Parses a date string using the dynamic format */
  override parse(value: any): Date | null {
    if (value instanceof Date) {
      return value;
    }
    if (moment.isMoment(value)) {
      return value.isValid() ? value.toDate() : null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const m: Moment = moment(value, this.currentFormat, true); // strict
      return m.isValid() ? m.toDate() : null;
    }
    return null;
  }

  /** Formats a date/time using moment. Respects Material display tokens and patterns. */
  override format(date: Date, displayFormat: any): string {
    if (!date) return '';
    const m = moment(date);

    // If Material passes a token or a pattern string, choose appropriate format
    if (typeof displayFormat === 'string') {
      // 'input' is our generic date token; otherwise treat string as moment pattern
      if (displayFormat === 'input') {
        return m.format(this.currentFormat);
      }
      return m.format(displayFormat);
    }

    // Handle potential object tokens (rare). Fallback to dynamic date format.
    return m.format(this.currentFormat);
  }

  // Add time helpers expected by MatTimepicker
  override parseTime(value: any, parseFormat: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value !== 'string' || value.trim() === '') return null;

    const candidates: string[] = [this.defaultTimeFormat, 'h:mm A', 'HH:mm'];

    // Resolve fmt from parseFormat token if provided
    let fmt: string | undefined;
    if (typeof parseFormat === 'string' && parseFormat.length > 0) {
      fmt = parseFormat === 'input' ? this.defaultTimeFormat : parseFormat;
      // Ensure provided fmt is first in candidates for strict try order
      candidates.unshift(fmt);
    }

    // Try formats in order until one parses
    let m: Moment | null = null;
    for (const f of candidates) {
      const attempt = moment(value.trim(), f, true);
      if (attempt.isValid()) {
        m = attempt;
        break;
      }
    }
    if (!m) return null;

    const now = moment();
    now.hours(m.hours());
    now.minutes(m.minutes());
    now.seconds(m.seconds() || 0);
    now.milliseconds(0);
    return now.toDate();
  }

  /** Ensure cloning respects timezone neutrality */
  override clone(date: Date): Date {
    return date ? new Date(date.getTime()) : date;
  }
}

export const DYNAMIC_MAT_DATE_FORMATS = {
  parse: {
    dateInput: 'input',
    // MatTimepicker requires parse.timeInput to be present; use 'input' token
    timeInput: 'input'
  },
  display: {
    dateInput: 'input',
    // Provide time display formats compatible with MatTimepicker
    timeInput: 'h.mm A',
    timeOptionLabel: 'h.mm A',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
